import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { SITE } from "../lib/site";

const NAV_LINKS = [
  { href: "#about", label: "About" },
  { href: "#services", label: "Services" },
  { href: "#sectors", label: "Sectors" },
  { href: "#menu", label: "Menu" },
  { href: "#why-us", label: "Why Us" },
  { href: "#clients", label: "Clients" },
  { href: "#testimonials", label: "Testimonials" },
  { href: "#contact", label: "Enquire" },
  { href: "/blog", label: "Blog" },
];

export default function Navigation() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#FDFBF7]/85 backdrop-blur-xl border-b border-[#1E3A5F]/10"
          : "bg-[#0E2238]/30 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 flex items-center justify-between h-20">
        <a
          href="#home"
          data-testid="nav-logo"
          className="flex items-center gap-3"
          aria-label="The Office Meal Co. — Home"
        >
          <img
            src={SITE.logoUrl}
            alt="The Office Meal Co."
            className="h-12 w-12 object-cover rounded-full border border-[#1E3A5F]/10 bg-[#FDFBF7]"
          />
          <div className="hidden sm:block leading-tight">
            <div
              className={`font-serif text-lg tracking-tight transition-colors ${
                scrolled ? "text-[#1E3A5F]" : "text-[#FDFBF7]"
              }`}
            >
              The Office Meal Co.
            </div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#CBA365]">
              Deliciously Managed
            </div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-10">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={`link-underline text-sm font-sans-brand transition-colors hover:text-[#CBA365] ${
                scrolled ? "text-[#1E3A5F]" : "text-[#FDFBF7]"
              }`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <a
          href="#contact"
          data-testid="nav-cta"
          className={`hidden lg:inline-flex items-center text-xs uppercase tracking-[0.2em] px-6 py-3 transition-all ${
            scrolled
              ? "bg-[#1E3A5F] text-[#FDFBF7] hover:bg-[#122644]"
              : "bg-[#CBA365] text-[#1E3A5F] hover:bg-[#FDFBF7]"
          }`}
        >
          Request a Quote
        </a>

        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Open menu"
          className={`lg:hidden p-2 transition-colors ${
            scrolled ? "text-[#1E3A5F]" : "text-[#FDFBF7]"
          }`}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="lg:hidden bg-[#FDFBF7] border-t border-[#1E3A5F]/10"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                className="text-[#1E3A5F] font-sans-brand text-base"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-cta"
              className="mt-2 inline-flex items-center justify-center bg-[#1E3A5F] text-[#FDFBF7] text-xs uppercase tracking-[0.2em] px-6 py-3"
            >
              Request a Quote
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
